import type { ComponentProps, ReactNode } from 'react';
import { merge } from 'lodash-es';
import React, { createContext, useContext, useMemo } from 'react';

import type { TabProps } from '../../controls/Tabs/Tabs';
import type { Breakpoint } from '../../utilities/useViewport';
import type { ShellProps } from '../Shell/Shell';
import type { ShellContentProps } from '../Shell/ShellContent';
import type { ShellFooterProps } from '../Shell/ShellFooter';
import type { ShellHeaderProps } from '../Shell/ShellHeader';
import { atContainers, containers } from '../../common/containers';
import { sizing } from '../../common/sizing';
import { styled } from '../../stitches.config';
import { useViewport } from '../../utilities/useViewport';
import {
  type DrawerWidthDescriptor,
  getDrawerPaddingValue,
  useDrawerWidthContext,
} from '../Drawer/Drawer';
import { Shell } from '../Shell/Shell';
import { ShellContent } from '../Shell/ShellContent';
import { ShellFooter } from '../Shell/ShellFooter';
import { ShellHeader } from '../Shell/ShellHeader';

function getPaneEffectiveDescriptor(
  contextDescriptor: DrawerWidthDescriptor | null,
): DrawerWidthDescriptor | undefined {
  if (contextDescriptor === null) return undefined;
  return contextDescriptor;
}

export type PaneContextValue = {
  /**
   * Descriptor for drawer-matched padding for this `Pane` only. Null when no drawer,
   * or when an ancestor {@link Pane} already applied drawer padding (nested `Pane`).
   */
  drawerDescriptor: DrawerWidthDescriptor | null;
  /**
   * True when the current `Pane` is a nested another `Pane`.
   */
  isAncestor: boolean;
  /**
   * True when a {@link Drawer} is mounted and registered.
   */
  isDrawerOpen: boolean;
};

const defaultPaneContextValue: PaneContextValue = {
  drawerDescriptor: null,
  isAncestor: false,
  isDrawerOpen: false,
};

export const PaneContext = createContext<PaneContextValue>(defaultPaneContextValue);

const PaneHeaderContainer = styled(ShellHeader);
PaneHeaderContainer.displayName = 'PaneHeaderContainer';

export type PaneHeaderProps = ShellHeaderProps & ComponentProps<typeof PaneHeaderContainer>;

export type PaneHeaderExtensions = {
  renderTabMenu?: (tab: TabProps, header: ShellHeaderProps, parent?: TabProps) => ReactNode;
};

/** Lets an app add tab menus to every `PaneHeader` without each page wiring them up. */
export const PaneHeaderExtensionsContext = createContext<PaneHeaderExtensions>({});

export function PaneHeader({ css, ...remaining }: PaneHeaderProps) {
  const { renderTabMenu } = useContext(PaneHeaderExtensionsContext);
  return (
    <PaneHeaderContainer
      type="pane"
      css={css}
      renderTabMenu={
        renderTabMenu &&
        ((tab: TabProps, parent?: TabProps) => renderTabMenu(tab, remaining, parent))
      }
      {...remaining}
    />
  );
}

const PaneContentContainer = styled(ShellContent);
PaneContentContainer.displayName = 'PaneContentContainer';

export type PaneContentProps = ShellContentProps & ComponentProps<typeof PaneContentContainer>;

export function PaneContent({
  children,
  gutter = { edge: 'none' },
  spacing = 16,
  ref,
  css,
  ...remaining
}: PaneContentProps) {
  return (
    <PaneContentContainer
      gutter={gutter}
      spacing={spacing}
      ref={ref}
      css={css}
      type="pane"
      {...remaining}
    >
      {children}
    </PaneContentContainer>
  );
}

const PaneFooterContainer = styled(ShellFooter);
PaneFooterContainer.displayName = 'PaneFooterContainer';

export type PaneFooterProps = ShellFooterProps & {
  /**
   * Provide primary actions for your drawer.
   */
  actions?: React.ReactNode;
  /**
   * Fallback to `children` if `actions` or `secondaryActions` are not provided.
   */
  children?: React.ReactNode;
  /**
   * Provide secondary actions, positioned at the start of the footer.
   */
  secondaryActions?: React.ReactNode;
} & ComponentProps<typeof PaneFooterContainer>;

export function PaneFooter({
  actions,
  children,
  secondaryActions,
  css,
  ...remaining
}: PaneFooterProps) {
  return (
    <PaneFooterContainer
      start={secondaryActions}
      end={actions || children}
      type="pane"
      css={css}
      {...remaining}
    />
  );
}

export const PaneContainer = styled(Shell, {
  gridArea: 'pane',
  width: '100%',

  '@mobile': {
    widthAll: '100%',
  },

  variants: {
    preset: {
      full: {},
      intermediate: {
        '@mobile': {
          height: '100%',
        },

        '@notMobile': {
          paddingTop: `$${sizing.primary}`,
        },
      },
      narrow: {
        '@mobile': {
          height: '100%',
        },

        '@notMobile': {
          paddingTop: `$${sizing.primary}`,
        },
      },
    },
  },
});
PaneContainer.displayName = 'PaneContainer';

export type ShellPresetProp = 'narrow' | 'intermediate' | 'full';

const PANE_PRESET_KEYS: Record<ShellPresetProp, true> = {
  full: true,
  intermediate: true,
  narrow: true,
};

/** All `ShellPresetProp` values for pane `preset`, aligned with `PaneContainer` variants. */
export const PANE_PRESET_VALUES = Object.keys(PANE_PRESET_KEYS) as ShellPresetProp[];

export type PaneProps = Omit<ShellProps, 'type'> & {
  preset?: ShellPresetProp;
} & ComponentProps<typeof PaneContainer>;

const getPaneMaxWidth = (preset: ShellPresetProp, breakpoint: Breakpoint) => {
  switch (preset) {
    case 'intermediate':
      return breakpoint !== 'mobile' ? 1024 : undefined;
    case 'narrow':
      return breakpoint !== 'mobile' ? 760 : undefined;
    default:
      return 'full';
  }
};

export function Pane({ children, preset = 'full', css, ...remaining }: PaneProps) {
  const { breakpoint } = useViewport();
  const { descriptor: contextDescriptor } = useDrawerWidthContext();
  const { isAncestor } = useContext(PaneContext);

  const effectivePaneDescriptor = getPaneEffectiveDescriptor(contextDescriptor);
  const applyPanePadding = effectivePaneDescriptor !== undefined && !isAncestor;

  const paneContextValue = useMemo<PaneContextValue>(
    () => ({
      drawerDescriptor: applyPanePadding ? contextDescriptor : null,
      isAncestor: isAncestor || applyPanePadding,
      isDrawerOpen: contextDescriptor !== null,
    }),
    [isAncestor, applyPanePadding, contextDescriptor],
  );

  const applyPanePaddingCSS =
    applyPanePadding && effectivePaneDescriptor !== undefined
      ? {
          '@desktop': {
            paddingRight: getDrawerPaddingValue(effectivePaneDescriptor),

            ...atContainers([containers.stage.narrow], { paddingRight: 0 }),
          },
        }
      : undefined;

  const effectiveCSS = merge(applyPanePaddingCSS, css);

  return (
    <PaneContext.Provider value={paneContextValue}>
      <PaneContainer
        preset={preset}
        maxWidth={getPaneMaxWidth(preset, breakpoint)}
        widthMode={preset === 'full' ? 'full' : 'centered'}
        css={effectiveCSS}
        {...remaining}
        type="pane"
      >
        {children}
      </PaneContainer>
    </PaneContext.Provider>
  );
}
